import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	fetchRestoreSelectionsValidation,
	type RestoreSelectionInput,
	type RestoreSelectionsValidationRequest,
	type RestoreSelectionsValidationResponse,
} from "../services/pricingService";
import { loadComponentSelections } from "../utils/componentSelections";

export const SESSION_RESTORE_VALIDATION_QUERY_KEY = "sessionRestoreValidation";

interface UseSessionRestoreValidationOptions {
	configBundleId: string;
	hasUrlParam: boolean;
	isUrlLoading: boolean;
	isUrlInvalidConfig: boolean;
}

function buildSelectionInputs(configBundleId: string): RestoreSelectionInput[] {
	const storedSelections = loadComponentSelections(configBundleId);

	return Object.entries(storedSelections).flatMap(([productGroupId, selection]) => {
		if (Array.isArray(selection)) {
			return selection.map((s) => ({ productGroupId, productId: s.productId }));
		}
		return [{ productGroupId, productId: selection.productId }];
	});
}

export function useSessionRestoreValidation({
	configBundleId,
	hasUrlParam,
	isUrlLoading,
	isUrlInvalidConfig,
}: UseSessionRestoreValidationOptions) {
	const selectionInputs = useMemo(() => {
		if (!configBundleId || hasUrlParam) {
			return [];
		}

		return buildSelectionInputs(configBundleId);
	}, [configBundleId, hasUrlParam]);

	const request = useMemo((): RestoreSelectionsValidationRequest | null => {
		if (!configBundleId || selectionInputs.length === 0) {
			return null;
		}

		return {
			configBundleId,
			selections: selectionInputs,
		};
	}, [configBundleId, selectionInputs]);

	const hasSelectableProducts = useMemo(() => {
		return selectionInputs.some((item) => !!item.productId);
	}, [selectionInputs]);

	const canValidateSessionRestore =
		!!request &&
		!hasUrlParam &&
		!isUrlLoading &&
		!isUrlInvalidConfig &&
		hasSelectableProducts;

	const selectionSignature = useMemo(
		() => JSON.stringify([...selectionInputs].sort((a, b) => a.productGroupId.localeCompare(b.productGroupId))),
		[selectionInputs],
	);

	return useQuery<RestoreSelectionsValidationResponse, Error>({
		queryKey: [SESSION_RESTORE_VALIDATION_QUERY_KEY, configBundleId, selectionSignature],
		queryFn: () => {
			if (!request) {
				throw new Error("Session restore validation request is not available");
			}

			return fetchRestoreSelectionsValidation(request);
		},
		enabled: canValidateSessionRestore,
		staleTime: 1000 * 60 * 5,
		gcTime: 1000 * 60 * 10,
		retry: 0,
	});
}
